import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Footer } from "./Footer";

const SECTIONS = [
  {
    n: "01",
    title: "About Saferide",
    blocks: [
      { type: "p", text: "Saferide is a technology platform that connects drivers and riders who wish to share transportation. Saferide does not own, operate, lease, or control vehicles and is not a transportation carrier, taxi service, or employer of drivers." },
      { type: "p", text: "Drivers and riders are solely responsible for their conduct before, during, and after each trip." },
    ],
  },
  {
    n: "02",
    title: "Eligibility",
    blocks: [
      { type: "lead", text: "To use Saferide, you must:" },
      { type: "ul", items: ["Be at least 18 years of age.", "Be legally capable of entering into binding agreements.", "Provide accurate and truthful information.", "Comply with all applicable laws."] },
      { type: "p", text: "Saferide may refuse service or terminate accounts that fail to meet these requirements." },
    ],
  },
  {
    n: "03",
    title: "User Accounts",
    blocks: [
      { type: "lead", text: "You agree to:" },
      { type: "ul", items: ["Maintain accurate account information.", "Keep your password confidential.", "Notify Saferide immediately of unauthorized account access.", "Accept responsibility for all activities conducted through your account."] },
      { type: "p", text: "Saferide reserves the right to suspend or terminate accounts suspected of fraud, misuse, or unauthorized access." },
    ],
  },
  {
    n: "04",
    title: "Driver Responsibilities",
    blocks: [
      { type: "lead", text: "Drivers agree to:" },
      { type: "ul", items: ["Hold a valid driver's licence.", "Maintain valid vehicle registration.", "Maintain insurance required by applicable law.", "Operate a safe and roadworthy vehicle.", "Follow all traffic laws.", "Arrive at pickup locations on time whenever reasonably possible.", "Treat riders respectfully and professionally."] },
      { type: "lead", text: "Drivers must not:" },
      { type: "ul", items: ["Drive while impaired by alcohol or drugs.", "Use an unsafe vehicle.", "Engage in harassment, discrimination, or abusive behaviour.", "Carry illegal items or prohibited substances."] },
    ],
  },
  {
    n: "05",
    title: "Rider Responsibilities",
    blocks: [
      { type: "lead", text: "Riders agree to:" },
      { type: "ul", items: ["Arrive at the agreed pickup location on time.", "Wear a seatbelt where required by law.", "Respect drivers, passengers, and property.", "Follow reasonable safety instructions.", "Avoid distracting the driver."] },
      { type: "lead", text: "Riders must not:" },
      { type: "ul", items: ["Damage vehicles.", "Smoke or vape without permission.", "Carry illegal or hazardous materials.", "Engage in threatening, abusive, or discriminatory behaviour."] },
    ],
  },
  {
    n: "06",
    title: "Bookings",
    blocks: [
      { type: "p", text: "Bookings are confirmed only after acceptance through the Saferide platform." },
      { type: "p", text: "Users are responsible for reviewing trip details before confirming a booking." },
      { type: "p", text: "Repeated cancellations or no-shows may result in account restrictions." },
    ],
  },
  {
    n: "07",
    title: "Payments",
    blocks: [
      { type: "p", text: "All payments must be processed through Saferide unless otherwise authorized." },
      { type: "lead", text: "Applicable fees may include:" },
      { type: "ul", items: ["Ride fare", "Platform service fee", "Payment processing fee", "Taxes required by law"] },
      { type: "p", text: "Prices displayed before booking are subject to applicable taxes and fees." },
    ],
  },
  {
    n: "08",
    title: "Cancellations and Refunds",
    blocks: [
      { type: "p", text: "Cancellation and refund eligibility is governed by Saferide's separate Cancellation Policy and Refund Policy." },
      { type: "p", text: "Approved refunds will generally be processed within 5–10 business days, depending on the payment provider." },
    ],
  },
  {
    n: "09",
    title: "Ratings and Reviews",
    blocks: [
      { type: "p", text: "Users may rate and review one another after completed trips." },
      { type: "lead", text: "Reviews must:" },
      { type: "ul", items: ["Be truthful.", "Be respectful.", "Not contain offensive, abusive, or defamatory content."] },
      { type: "p", text: "Saferide may remove reviews that violate these Terms." },
    ],
  },
  {
    n: "10",
    title: "Safety",
    blocks: [
      { type: "p", text: "All users are expected to prioritize safety." },
      { type: "p", text: "In emergencies, users should immediately contact local emergency services before contacting Saferide." },
      { type: "p", text: "Saferide reserves the right to investigate safety complaints and suspend accounts while investigations are ongoing." },
    ],
  },
  {
    n: "11",
    title: "Prohibited Conduct",
    blocks: [
      { type: "lead", text: "Users must not:" },
      { type: "ul", items: ["Commit fraud.", "Use false identities.", "Share accounts.", "Harass or threaten others.", "Discriminate based on race, religion, gender, disability, sexual orientation, age, or any other protected characteristic.", "Transport prohibited items.", "Circumvent platform fees.", "Use Saferide for unlawful purposes."] },
    ],
  },
  {
    n: "12",
    title: "Account Suspension and Termination",
    blocks: [
      { type: "lead", text: "Saferide may suspend or permanently terminate accounts for:" },
      { type: "ul", items: ["Violating these Terms.", "Fraudulent activity.", "Criminal conduct.", "Repeated cancellations.", "Unsafe behaviour.", "Failure to maintain required driver documentation.", "Abuse of the platform."] },
      { type: "p", text: "Termination may occur without prior notice where necessary to protect users or comply with legal obligations." },
    ],
  },
  {
    n: "13",
    title: "Intellectual Property",
    blocks: [
      { type: "p", text: "All content on the Saferide platform, including logos, trademarks, software, graphics, and text, is owned by or licensed to Saferide and is protected by applicable intellectual property laws." },
      { type: "p", text: "Users may not copy, modify, distribute, or reproduce any content without prior written permission." },
    ],
  },
  {
    n: "14",
    title: "Privacy",
    blocks: [
      { type: "p", text: "Your use of Saferide is subject to the Saferide Privacy Policy, which explains how we collect, use, and protect your personal information." },
    ],
  },
  {
    n: "15",
    title: "Limitation of Liability",
    blocks: [
      { type: "lead", text: "To the fullest extent permitted by law, Saferide shall not be liable for:" },
      { type: "ul", items: ["Personal injury.", "Death.", "Property damage.", "Vehicle damage.", "Lost profits.", "Missed appointments.", "Travel delays.", "Acts or omissions of drivers or riders.", "Disputes between users."] },
      { type: "p", text: "Nothing in these Terms excludes liability that cannot legally be excluded under applicable law." },
    ],
  },
  {
    n: "16",
    title: "Indemnification",
    blocks: [
      { type: "lead", text: "You agree to indemnify and hold harmless Saferide, its officers, directors, employees, affiliates, and partners from claims, losses, liabilities, damages, costs, and legal expenses arising from:" },
      { type: "ul", items: ["Your misuse of the Services.", "Your violation of these Terms.", "Your violation of applicable laws.", "Your conduct toward other users."] },
    ],
  },
  {
    n: "17",
    title: "Force Majeure",
    blocks: [
      { type: "p", text: "Saferide shall not be responsible for delays or failures caused by events beyond its reasonable control, including severe weather, natural disasters, pandemics, strikes, internet outages, government actions, or other unforeseen events." },
    ],
  },
  {
    n: "18",
    title: "Changes to the Services",
    blocks: [
      { type: "p", text: "Saferide may modify, suspend, or discontinue any part of the Services at any time without prior notice." },
    ],
  },
  {
    n: "19",
    title: "Changes to These Terms",
    blocks: [
      { type: "p", text: "Saferide may revise these Terms from time to time." },
      { type: "p", text: "Updated Terms will be posted within the app or on our website. Continued use of the Services after changes become effective constitutes acceptance of the revised Terms." },
    ],
  },
  {
    n: "20",
    title: "Governing Law",
    blocks: [
      { type: "p", text: "These Terms shall be governed by and interpreted in accordance with the laws of the Province of Alberta and the federal laws of Canada applicable therein." },
      { type: "p", text: "Any disputes arising from these Terms shall be resolved in the courts of Alberta, unless otherwise required by applicable law." },
    ],
  },
  {
    n: "21",
    title: "Contact Information",
    blocks: [
      { type: "p", text: "For questions regarding these Terms and Conditions, please contact:" },
      { type: "contact" },
    ],
  },
  {
    n: "22",
    title: "Acceptance",
    blocks: [
      { type: "p", text: "By creating an account, booking a ride, offering a ride, or otherwise using the Saferide platform, you confirm that you have read, understood, and agree to be bound by these Terms and Conditions." },
    ],
  },
];

const Block = ({ block }) => {
  if (block.type === "p")
    return <p className="mb-4 text-base leading-relaxed text-[#565b73]">{block.text}</p>;
  if (block.type === "lead")
    return <p className="mb-4 mt-6 font-display text-base font-semibold text-[#12142b] first:mt-0">{block.text}</p>;
  if (block.type === "ul")
    return (
      <ul className="mb-4 space-y-2.5">
        {block.items.map((it) => (
          <li key={it} className="flex gap-3 text-base leading-relaxed text-[#565b73]">
            <span className="mt-2.5 h-1.5 w-1.5 shrink-0 bg-[#2340b5]" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  if (block.type === "contact")
    return (
      <div className="mt-2 border border-[#12142b]/10 bg-[#f4f6fb] p-6">
        <p className="font-display text-lg font-bold text-[#12142b]">Saferide Support</p>
        <p className="mt-3 text-base text-[#565b73]">
          Email:{" "}
          <a href="mailto:saferidecarppool@gmail.com" className="text-[#2340b5] underline-offset-4 hover:underline">
            saferidecarppool@gmail.com
          </a>
        </p>
        <p className="mt-1 text-base text-[#565b73]">
          Website:{" "}
          <a href="https://www.mysaferide.ca" target="_blank" rel="noreferrer" className="text-[#2340b5] underline-offset-4 hover:underline">
            www.mysaferide.ca
          </a>
        </p>
      </div>
    );
  return null;
};

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#12142b]/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1000px] items-center justify-between px-6 py-4 lg:px-8">
          <Link to="/" data-testid="terms-logo" className="flex items-center gap-2.5">
            <img src="/saferide-mark.png" alt="Saferide" className="h-8 w-auto" />
            <span className="font-display text-lg font-extrabold tracking-tight text-[#12142b]">
              Safe<span className="text-[#2340b5]">ride</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-home"
            className="group inline-flex items-center gap-2 font-display text-sm font-medium text-[#565b73] transition-colors hover:text-[#2340b5]"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" strokeWidth={1.5} />
            Back home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="mx-auto max-w-[1000px] px-6 pb-12 pt-20 lg:px-8 lg:pt-28">
        <p className="mb-6 flex items-center gap-3 font-display text-xs uppercase tracking-[0.28em] text-[#2340b5]">
          <span className="h-px w-10 bg-[#2340b5]" /> Legal
        </p>
        <h1 className="font-display text-4xl font-extrabold leading-none tracking-tighter text-[#12142b] sm:text-5xl lg:text-6xl">
          Terms & Conditions
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#565b73]">
          Please read these Terms and Conditions carefully before using the Saferide platform.
          By accessing or using our Services, you agree to be bound by these Terms.
        </p>
      </div>

      {/* Sections */}
      <main className="mx-auto max-w-[1000px] px-6 pb-24 lg:px-8">
        <div className="border-t border-[#12142b]/10">
          {SECTIONS.map((s) => (
            <section
              key={s.n}
              data-testid={`terms-section-${s.n}`}
              className="grid grid-cols-1 gap-6 border-b border-[#12142b]/10 py-12 md:grid-cols-12 md:gap-10"
            >
              <div className="md:col-span-4">
                <div className="flex items-baseline gap-4 md:sticky md:top-24">
                  <span className="font-display text-3xl font-black leading-none text-[#12142b]/10">{s.n}</span>
                  <h2 className="font-display text-xl font-bold tracking-tight text-[#12142b] lg:text-2xl">
                    {s.title}
                  </h2>
                </div>
              </div>
              <div className="md:col-span-8">
                {s.blocks.map((b, i) => (
                  <Block key={i} block={b} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </main>

      <Footer />
    </div>
  );
}
